import { Controller, useForm } from "react-hook-form";
import TextInput from "../../ui/TextInput/TextInput";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import urls from "../../routes";
import { graphql } from "../../__generated__/gql";
import { useMutation, useQuery, useSubscription } from "urql";
import Logo from "../../ui/Logo/Logo";
import Button from "../../ui/Button/Button";
import background from "./Background.webp";
import marty from "./marty.webp";
import { AlertTriangle, Close } from "../../ui/Icon/Icon";
import useUser from "../../providers/UserProvider/useUser";
import freeDomains from "./free-domains";
import { useToast } from "../../ui/Toast/ToastContext";
import { useClerk } from "@clerk/clerk-react";
import classNames from "classnames";
import Card from "../../ui/Card/Card";
import Label from "../../ui/Label/Label";
import IconButton from "../../ui/IconButton/IconButton";
import Audience from "./Audience";
// import CustomTone from "./CustomTone";
import Description from "./Description";
import TypedText from "./TypedText";
import { Transition, TransitionStatus } from "react-transition-group";
import Loader from "../../ui/Loader/Loader";
import KeywordSuggestion from "./KeywordSuggestion";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Feedback from "../../ui/Feedback/Feedback";
import styles from "./Onboarding.module.scss";
import Meeting from "./Meeting";

const getUrl = (value: string) => {
  if (!value.startsWith("http://") && !value.startsWith("https://")) {
    return new URL(`https://${value}`);
  }

  return new URL(value);
};

const OnboardingWebsiteSettingsQuery = graphql(/* GraphQL */ `
  query onboardingWebsiteSettings {
    websiteSettings {
      websites
    }
    billingInfo {
      freeCreditsIssued
    }
    location {
      country
      countryCode
    }
  }
`);

const OnboardingBrandSettingsQuery = graphql(/* GraphQL */ `
  query onboardingBrandSettings {
    brandSettings {
      companyName
      companyDescription
      logoUrl
      audience
      customTone
    }
  }
`);

const IsWebsiteReachableMutation = graphql(/* GraphQL */ `
  mutation isWebsiteReachable($url: String!) {
    isWebsiteReachable(url: $url)
  }
`);

const StartOnboardingMutation = graphql(/* GraphQL */ `
  mutation startOnboarding($input: StartOnboardingInput!) {
    startOnboarding(input: $input) {
      id
    }
  }
`);

const OnboardingTimedOutMutation = graphql(/* GraphQL */ `
  mutation onboardingTimedOut {
    onboardingTimedOut {
      id
    }
  }
`);

const BrandSettingsUpdatedSubscription = graphql(/* GraphQL */ `
  subscription brandSettingsUpdated {
    brandSettingsUpdated {
      companyName
      companyDescription
      logoUrl
      audience
      customTone
    }
  }
`);

const FinishOnboardingMutation = graphql(/* GraphQL */ `
  mutation finishOnboarding {
    finishOnboarding {
      id
      isOnboardingComplete
    }
  }
`);

const UpdateBrandNameMutation = graphql(/* GraphQL */ `
  mutation updateBrandName($name: String!) {
    updateBrandSettings(input: { companyName: $name }) {
      companyName
    }
  }
`);

const restrictedDomains = [
  "instagram.com",
  "facebook.com",
  "twitter.com",
  "linkedin.com",
  "youtube.com",
  "google.com",
  "x.com",
  "app.macawhq.com",
  "example.com",
  "vercel.app",
];

const siteSelectionFormSchema = z.object({
  site: z
    .string()
    .refine(
      (value) => {
        try {
          const url = getUrl(value);

          if (url.host === "agix-olive.vercel.app") {
            return true;
          }

          return !restrictedDomains.some(
            (restrictedDomain) =>
              url.host.toLowerCase() === restrictedDomain ||
              url.host.includes(`.${restrictedDomain}`),
          );
        } catch {
          return false;
        }
      },
      {
        message: "You must enter your own website to continue",
      },
    )
    .refine(
      (value) => {
        try {
          const url = getUrl(value);

          return url.pathname === "/" || url.pathname === "";
        } catch {
          return false;
        }
      },
      {
        message: "Please enter your website's base URL without a path",
      },
    ),
});

function InternalSiteSelection({
  ref,
  className,
  defaultSite,
  onSubmit,
}: {
  ref: React.Ref<HTMLDivElement>;
  className?: string;
  defaultSite?: string;
  onSubmit: (data: { site: string }) => void;
}) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm<{ site: string }>({
    defaultValues: { site: defaultSite },
    resolver: zodResolver(siteSelectionFormSchema),
  });

  const site = watch("site");

  return (
    <Card className={classNames(styles.card, className)} ref={ref}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.textInputStep}>
        <Label label="Website URL" htmlFor="site">
          <Controller
            control={control}
            name="site"
            render={({ field, fieldState: { error } }) => (
              <>
                <TextInput {...field} />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </>
            )}
          />
        </Label>
        <Button
          className={styles.button}
          type="submit"
          variant="primary"
          size="small"
          loading={isSubmitting}
          disabled={isSubmitting || !site}
        >
          Continue
        </Button>
      </form>
    </Card>
  );
}

function SiteSelection({
  ref,
  className,
  onSubmit,
  site,
}: {
  ref: React.Ref<HTMLDivElement>;
  className?: string;
  onSubmit: (data: { site: string }) => void;
  site?: string;
}) {
  return (
    <InternalSiteSelection
      ref={ref}
      className={className}
      defaultSite={site}
      onSubmit={onSubmit}
    />
  );
}

function BrandDetails({
  ref,
  state,
  step,
  setStep,
  setMessage,
  retry,
  imageLoaded,
  setImageLoaded,
  imageLoadedError,
  setImageLoadedError,
  site,
  hasFreeCredits,
}: {
  ref: React.Ref<HTMLDivElement>;
  state: TransitionStatus;
  className?: string;
  step: Step;
  setStep: (step: Step) => void;
  setMessage: (message: string) => void;
  retry: () => void;
  imageLoaded: boolean;
  setImageLoaded: (imageLoaded: boolean) => void;
  imageLoadedError: boolean;
  setImageLoadedError: (imageLoadedError: boolean) => void;
  site?: string;
  hasFreeCredits: boolean;
}) {
  const [ready, setReady] = useState(false);
  const [delayComplete, setDelayComplete] = useState(false);
  const [previewDelayComplete, setPreviewDelayComplete] = useState(false);
  const [, updateBrandName] = useMutation(UpdateBrandNameMutation);
  const [, onboardingTimedOut] = useMutation(OnboardingTimedOutMutation);
  const [localCompanyName, setLocalCompanyName] = useState<string>();
  const [brandSettingsSubscription] = useSubscription({
    query: BrandSettingsUpdatedSubscription,
  });
  const [erroring, setErroring] = useState(false);
  const [errored, setErrored] = useState(false);
  const [windowFocused, setWindowFocused] = useState(true);
  const imageRef = useRef<HTMLImageElement>(null);

  const [brandSettingsResponse, refetchBrandSettings] = useQuery({
    query: OnboardingBrandSettingsQuery,
    requestPolicy: "network-only",
  });

  const logoUrl =
    brandSettingsResponse.data?.brandSettings?.logoUrl ??
    brandSettingsSubscription.data?.brandSettingsUpdated.logoUrl;

  const companyName =
    brandSettingsResponse.data?.brandSettings?.companyName ??
    brandSettingsSubscription.data?.brandSettingsUpdated.companyName;

  const companyDescription =
    brandSettingsResponse.data?.brandSettings?.companyDescription ??
    brandSettingsSubscription.data?.brandSettingsUpdated.companyDescription;

  const audience =
    brandSettingsResponse.data?.brandSettings?.audience ??
    brandSettingsSubscription.data?.brandSettingsUpdated.audience;

  // const customTone =
  //   brandSettingsResponse.data?.brandSettings?.customTone ??
  //   brandSettingsSubscription.data?.brandSettingsUpdated.customTone;

  useEffect(() => {
    window.addEventListener("focus", () => {
      setWindowFocused(true);
    });

    window.addEventListener("blur", () => {
      setWindowFocused(false);
    });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        erroring &&
        !errored &&
        !brandSettingsResponse.fetching &&
        windowFocused
      ) {
        if (!brandSettingsResponse.data?.brandSettings?.companyName) {
          void onboardingTimedOut({});
          setMessage("Oops...");
          setErrored(true);
        } else {
          setErroring(false);
        }
      }
    }, 1_000);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    brandSettingsResponse,
    errored,
    erroring,
    onboardingTimedOut,
    setMessage,
    windowFocused,
  ]);

  useEffect(() => {
    if (!imageLoaded && imageRef.current) {
      const timeout = setTimeout(() => {
        setImageLoadedError(true);
        setImageLoaded(true);
      }, 30_000);

      const interval = setInterval(() => {
        if (imageRef.current?.complete) {
          setImageLoaded(true);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    } else {
      setErrored(false);
    }
  }, [imageLoaded, setImageLoaded, setImageLoadedError]);

  useEffect(() => {
    if (companyName) {
      setLocalCompanyName((previous) => (!previous ? companyName : previous));
    }
  }, [companyName]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (localCompanyName && companyName !== localCompanyName) {
        void updateBrandName({ name: localCompanyName });
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [companyName, localCompanyName, updateBrandName]);

  useEffect(() => {
    if (step === "preview") {
      setMessage("Great! Lets take a look...");

      setReady(false);
      setDelayComplete(false);

      const delayTimeout = setTimeout(() => {
        setDelayComplete(true);
      }, 2000);

      return () => {
        clearTimeout(delayTimeout);
      };
    } else {
      setReady(true);
      setDelayComplete(true);
    }
  }, [setImageLoaded, setMessage, setStep, step]);

  useEffect(() => {
    if (step === "preview" && imageLoaded && delayComplete) {
      if (!ready) {
        setReady(true);

        if (imageLoadedError) {
          setPreviewDelayComplete(true);
        } else {
          setMessage("Hey, nice website!");
          setPreviewDelayComplete(false);
        }
      } else {
        const delayTimeout = setTimeout(() => {
          setPreviewDelayComplete(true);
        }, 2000);

        const messageOneTimeout = setTimeout(() => {
          setMessage("One moment while I take a look...");
        }, 3000);

        const timeouts = [
          6_000, 9_000, 12_000, 15_000, 18_000, 21_000, 24_000, 27_000, 30_000,
          33_000, 36_000, 39_000, 42_000, 45_000, 48_000, 51_000, 54_000,
          57_000,
        ].map((time, index) => {
          return setTimeout(() => {
            refetchBrandSettings();
            setMessage(index % 2 ? "Still looking..." : "Still looking... ");
          }, time);
        });

        return () => {
          clearTimeout(delayTimeout);
          clearTimeout(messageOneTimeout);
          timeouts.forEach((timeout) => clearTimeout(timeout));
        };
      }
    }
  }, [
    delayComplete,
    imageLoaded,
    imageLoadedError,
    ready,
    refetchBrandSettings,
    setMessage,
    setStep,
    step,
  ]);

  useEffect(() => {
    if (previewDelayComplete && step === "preview" && ready && companyName) {
      setStep("description");
    }
  }, [companyName, previewDelayComplete, ready, setStep, step]);

  useEffect(() => {
    if (!companyName) {
      const timeout = setTimeout(() => {
        refetchBrandSettings();
        setErroring(true);
      }, 60_000);

      return () => {
        clearTimeout(timeout);
      };
    } else {
      setErroring(false);
      setErrored(false);
    }
  }, [companyName, onboardingTimedOut, refetchBrandSettings, setMessage]);

  const refA = useRef<HTMLDivElement>(null);
  const refB = useRef<HTMLDivElement>(null);
  const refC = useRef<HTMLDivElement>(null);
  const refD = useRef<HTMLDivElement>(null);

  return (
    <div ref={ref} style={{ width: "100%", height: "100%", display: "flex" }}>
      <Transition
        in={errored && !["exiting", "exited"].includes(state)}
        timeout={200}
        appear
        mountOnEnter
        unmountOnExit
        nodeRef={refA}
      >
        {(state) => (
          <Card
            ref={refA}
            className={classNames(styles.card, styles.errorCard, {
              [styles.cardEntering]: state === "entering",
              [styles.cardExiting]: state === "exiting",
            })}
          >
            <h2>Something went wrong</h2>
            <p>
              Our team has been notified. Please refresh and try again. If this
              issue persists, please contact us.
            </p>
            <div className={styles.errorCardButtons}>
              <Button variant="primary" size="small" onClick={retry}>
                Retry
              </Button>
              <Button
                variant="outline"
                size="small"
                onClick={() => window.Intercom("showNewMessage")}
              >
                Contact us
              </Button>
            </div>
          </Card>
        )}
      </Transition>

      <Transition
        in={step === "preview" && (!ready || !imageLoaded) && !errored}
        timeout={200}
        appear
        mountOnEnter
        unmountOnExit
        nodeRef={refB}
      >
        {(state) => (
          <Loader
            ref={refB}
            className={classNames(styles.loader, {
              [styles.entering]: state === "entering",
              [styles.exiting]: state === "exiting",
            })}
          />
        )}
      </Transition>

      <Transition
        in={
          ready &&
          imageLoaded &&
          !errored &&
          ["preview", "description", "audience", "customTone"].includes(step) &&
          !["exiting", "exited"].includes(state)
        }
        timeout={200}
        appear
        nodeRef={refC}
      >
        {(state) => (
          <div
            ref={refC}
            style={{
              width: "100%",
              height: "100%",
              display: state === "exited" ? "none" : "flex",
            }}
          >
            <div
              className={classNames(styles.websitePreview, {
                [styles.websitePreviewEntered]: state === "entered",
                [styles.websitePreviewEntering]: state === "entering",
                [styles.websitePreviewExiting]: state === "exiting",
                [styles.websitePreviewExited]: state === "exited",
              })}
            >
              {!imageLoadedError && (
                <img
                  ref={imageRef}
                  key={site}
                  alt=""
                  src={`https://api.urlbox.io/v1/FS00dsvat3ngUCLe/webp?url=${site}&hide_cookie_banners=true&width=1920&height=1080&click_accept=true&user_agent=desktop&block_ads=true`}
                  className={classNames(styles.websitePreviewImage, {
                    [styles.websitePreviewImageSkewed]: step !== "preview",
                  })}
                />
              )}
            </div>

            {state !== "exited" && (
              <div className={styles.details}>
                <Transition
                  in={["description", "audience", "customTone"].includes(step)}
                  timeout={200}
                  appear
                  mountOnEnter
                  unmountOnExit
                  nodeRef={refD}
                >
                  {(innerState) => (
                    <Card
                      ref={refD}
                      className={classNames(styles.detailsCard, {
                        [styles.detailsCardEntering]: innerState === "entering",
                        [styles.detailsCardExiting]: innerState === "exiting",
                      })}
                    >
                      <div className={styles.detailsCardBody}>
                        <div className={styles.detailsCardHeader}>
                          {logoUrl && (
                            <img src={logoUrl} alt="" className={styles.logo} />
                          )}
                          <div className={styles.companyNameAndSite}>
                            <TextInput
                              className={styles.companyName}
                              unstyled
                              value={localCompanyName ?? ""}
                              onChange={(e) =>
                                setLocalCompanyName(e.target.value)
                              }
                            />

                            <span className={styles.site}>{site}</span>
                          </div>
                        </div>

                        {step === "description" && (
                          <Description
                            setMessage={setMessage}
                            onBack={() => {
                              setReady(false);
                              setStep("site");
                            }}
                            onNext={() => setStep("audience")}
                            companyDescription={companyDescription}
                          />
                        )}
                        {step === "audience" && (
                          <Audience
                            setMessage={setMessage}
                            onBack={() => setStep("description")}
                            onNext={() => {
                              if (hasFreeCredits) {
                                setStep("keywordSuggestion");
                              } else {
                                setStep("end");
                              }
                            }}
                            disabled={innerState === "exiting"}
                            // onNext={() => setStep("end")}
                            // onNext={() => setStep("customTone")}
                            audience={audience}
                          />
                        )}
                      </div>
                    </Card>
                  )}
                </Transition>
              </div>
            )}
          </div>
        )}
      </Transition>
    </div>
  );
}

type Step =
  | "site"
  | "preview"
  | "description"
  | "audience"
  | "keywordSuggestion"
  | "end"
  | "meeting";

function InnerOnboarding({
  ready,
  onSubmit,
  onRetry,
  step,
  setStep,
  site,
  imageLoaded,
  setImageLoaded,
  imageLoadedError,
  setImageLoadedError,
  hasFreeCredits,
  isFreeDomain,
  isFirstTeam,
  isHighValueCountry,
}: {
  ready: boolean;
  onSubmit: (data: { site: string }) => void;
  onRetry: () => void;
  step: Step;
  setStep: (step: Step) => void;
  site?: string;
  imageLoaded: boolean;
  setImageLoaded: (imageLoaded: boolean) => void;
  imageLoadedError: boolean;
  setImageLoadedError: (imageLoadedError: boolean) => void;
  hasFreeCredits: boolean;
  isFreeDomain: boolean;
  isFirstTeam: boolean;
  isHighValueCountry: boolean;
}) {
  const [message, setMessage] = useState("Let's get started!");
  const navigate = useNavigate();
  const [, finishOnboarding] = useMutation(FinishOnboardingMutation);
  const { setActive } = useClerk();
  const initialSite = useRef<string>(null);

  useEffect(() => {
    if (ready && !initialSite.current) {
      initialSite.current = site ?? null;
    }
  }, [ready, site]);

  const previousOrganizationId = localStorage.getItem("previousOrganizationId");

  const showMeeting = !isFreeDomain && isFirstTeam && isHighValueCountry;

  const finaliseOnboarding = useCallback(async () => {
    await finishOnboarding({});
    await navigate(
      hasFreeCredits ? urls.home : `${urls.home}?second-team=true`,
    );
  }, [finishOnboarding, hasFreeCredits, navigate]);

  useEffect(() => {
    if (step === "site" && ready) {
      setMessage(
        !initialSite.current
          ? "Please enter your website URL"
          : "Is this your website?",
      );
    }
  }, [ready, step]);

  useEffect(() => {
    if (step === "keywordSuggestion") {
      setMessage("Fantastic!");

      setTimeout(() => {
        setMessage("Okay, one last thing");
      }, 2000);

      setTimeout(() => {
        setMessage("Time to pick the first keyword for me to write about");
      }, 5000);
    }
  }, [finishOnboarding, navigate, step]);

  useEffect(() => {
    if (step === "end") {
      if (hasFreeCredits) {
        setMessage("Nice pick!");
      } else {
        setMessage("Fantastic!");
      }

      if (showMeeting) {
        setTimeout(() => {
          setMessage("Ok we're all setup.");
        }, 2000);

        setTimeout(() => {
          setStep("meeting");
        }, 5000);
      } else {
        setTimeout(() => {
          setMessage("Ok we're all setup, let's jump in.");
        }, 2000);

        setTimeout(() => {
          void finaliseOnboarding();
        }, 5000);
      }
    }
  }, [
    finaliseOnboarding,
    finishOnboarding,
    hasFreeCredits,
    setStep,
    showMeeting,
    step,
  ]);

  const onEnd = () => {
    setStep("end");
  };

  const refA = useRef<HTMLDivElement>(null);
  const refB = useRef<HTMLDivElement>(null);
  const refC = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Logo className={styles.macawLogo} />
        {previousOrganizationId && step !== "end" && (
          <IconButton
            className={styles.backButton}
            onClick={async () => {
              await setActive({
                organization: previousOrganizationId,
              });
              window.location.href = urls.home;
            }}
            IconComponent={Close}
            aria-label="Cancel"
          />
        )}
      </div>
      {step === "meeting" ? (
        <div className={styles.body}>
          <Meeting onSkip={finaliseOnboarding} />
        </div>
      ) : (
        <>
          <div className={styles.body}>
            {site && !imageLoadedError && (
              <img
                key={site}
                alt=""
                src={`https://api.urlbox.io/v1/FS00dsvat3ngUCLe/webp?url=${site}&hide_cookie_banners=true&width=1920&height=1080&click_accept=true&user_agent=desktop&block_ads=true`}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            )}

            {ready ? (
              <>
                <Transition
                  in={step === "site"}
                  timeout={200}
                  appear
                  mountOnEnter
                  unmountOnExit
                  nodeRef={refA}
                >
                  {(state) => (
                    <SiteSelection
                      ref={refA}
                      className={classNames({
                        [styles.cardEntering]: state === "entering",
                        [styles.cardExiting]: state === "exiting",
                      })}
                      onSubmit={onSubmit}
                      site={site}
                    />
                  )}
                </Transition>

                <Transition
                  in={[
                    "preview",
                    "description",
                    "audience",
                    "customTone",
                  ].includes(step)}
                  timeout={200}
                  appear
                  mountOnEnter
                  unmountOnExit
                  nodeRef={refB}
                >
                  {(state) => (
                    <BrandDetails
                      ref={refB}
                      state={state}
                      site={site}
                      step={step}
                      setStep={setStep}
                      setMessage={setMessage}
                      retry={onRetry}
                      imageLoaded={imageLoaded}
                      setImageLoaded={setImageLoaded}
                      imageLoadedError={imageLoadedError}
                      setImageLoadedError={setImageLoadedError}
                      hasFreeCredits={hasFreeCredits}
                    />
                  )}
                </Transition>

                <Transition
                  in={step === "keywordSuggestion"}
                  timeout={200}
                  appear
                  mountOnEnter
                  unmountOnExit
                  nodeRef={refC}
                >
                  {(state) => (
                    <KeywordSuggestion
                      ref={refC}
                      state={state}
                      setMessage={setMessage}
                      onNext={onEnd}
                    />
                  )}
                </Transition>
              </>
            ) : (
              <Loader />
            )}
          </div>
          <div className={styles.footer}>
            <div className={styles.leftSide}>
              <div className={styles.speechBubble}>
                <div className={styles.speechBubbleInner}>
                  <TypedText text={message} />
                  &nbsp;
                </div>
                <svg
                  className={styles.speechBubbleTriangle}
                  viewBox="0 0 32 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M23.8897 26.1777L2.599 8.87904C-0.65492 6.23523 1.21454 0.969762 5.40712 0.969762L26.6978 0.969764C29.1572 0.969764 31.1509 2.9635 31.1509 5.4229L31.1509 22.7216C31.1509 26.4713 26.7999 28.5423 23.8897 26.1777Z" />
                </svg>
              </div>
            </div>
            <div>
              <img src={marty} alt="" className={styles.macaw} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DeviceNotSupported({
  isFirstTime,
  loading,
}: {
  isFirstTime: boolean;
  loading: boolean;
}) {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Logo className={styles.macawLogo} />
      </div>
      <div className={styles.body}>
        {loading ? (
          <Loader />
        ) : (
          <Card className={classNames(styles.notSupportedCard)}>
            <div className={styles.notSupportedCardHeader}>
              Device not supported
            </div>
            <div className={styles.notSupportedCardBody}>
              <AlertTriangle
                size="large"
                className={styles.notSupportedCardIcon}
              />
              <p>
                {isFirstTime
                  ? "Macaw currently only supports desktop screen sizes. We have sent you an email to continue your setup when you're back at a computer."
                  : "Macaw currently only supports desktop screen sizes. Everything is ready for you to continue next time you're at your computer."}
              </p>
            </div>
            <div className={styles.notSupportedCardFooter}>
              <Button
                variant="primary"
                size="small"
                onClick={() => (window.location.href = "https://macawhq.com")}
              >
                Back to website
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function Onboarding() {
  const [size, setSize] = useState({
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
  });
  const called = useRef(false);
  const [ready, setReady] = useState(false);
  const [timerComplete, setTimerComplete] = useState(false);
  const { user, userOrganizations } = useUser();
  const userDomain = user?.email ? user.email.split("@")[1] : "";
  const isFreeDomain = freeDomains.includes(userDomain) || !userDomain;
  const [site, setSite] = useState<string>();
  const [, startOnboarding] = useMutation(StartOnboardingMutation);
  const [, isWebsiteReachable] = useMutation(IsWebsiteReachableMutation);
  const { showToast } = useToast();
  const [onboardingWebsiteSettingsResponse] = useQuery({
    query: OnboardingWebsiteSettingsQuery,
    requestPolicy: "network-only",
  });
  const [step, setStep] = useState<Step>("site");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageLoadedError, setImageLoadedError] = useState(false);
  const isFirstTime =
    !onboardingWebsiteSettingsResponse.data?.websiteSettings?.websites?.length;

  const isMobile = size.innerWidth < 768;

  const isHighValueCountry = onboardingWebsiteSettingsResponse.data?.location
    ?.countryCode
    ? !["IN", "PK", "BR", "TR", "UA"].includes(
        onboardingWebsiteSettingsResponse.data.location.countryCode,
      )
    : false;

  const hasFreeCredits =
    !!onboardingWebsiteSettingsResponse.data?.billingInfo.freeCreditsIssued;

  const onSubmit = async (data: { site: string }) => {
    if (data.site === site) {
      setStep("preview");

      return;
    }

    if (data.site.includes("@")) {
      console.log(`Site cannot contain @ symbol: ${data.site}`);

      showToast({
        title: "Website can't be reached",
        description: "Please check the URL and try again",
        variant: "error",
      });

      return;
    }

    setImageLoaded(false);

    let newSite = data.site;

    if (!newSite.startsWith("http://") && !newSite.startsWith("https://")) {
      newSite = `https://${newSite}`;
    }

    const isWebsiteReachableResponse = await isWebsiteReachable({
      url: newSite,
    });

    if (!isWebsiteReachableResponse.data?.isWebsiteReachable) {
      console.log(`Site not reachable: ${data.site}`);

      return showToast({
        title: "Website can't be reached",
        description: "Please check the URL and try again",
        variant: "error",
      });
    }

    setSite(newSite);

    await startOnboarding({ input: { site: newSite, isMobile } });

    setStep("preview");
  };

  const onRetry = useCallback(async () => {
    setStep("site");

    if (site) {
      await startOnboarding({ input: { site, isMobile } });
    }
  }, [isMobile, site, startOnboarding]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTimerComplete(true);
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useLayoutEffect(() => {
    function updateSize() {
      setSize({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    }

    window.addEventListener("resize", updateSize);

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    async function preloadOnboarding(site: string) {
      const isWebsiteReachableResponse = await isWebsiteReachable({
        url: site,
      });

      if (!isWebsiteReachableResponse.data?.isWebsiteReachable) {
        site = `https://www.${new URL(site).host}`;

        const isWwwwWebsiteReachableResponse = await isWebsiteReachable({
          url: site,
        });

        if (!isWwwwWebsiteReachableResponse.data?.isWebsiteReachable) {
          return setReady(true);
        }
      }

      setSite(site);
      setReady(true);
      await startOnboarding({ input: { site, isMobile } });
    }

    if (!onboardingWebsiteSettingsResponse.fetching && !called.current) {
      called.current = true;

      const savedSite =
        onboardingWebsiteSettingsResponse.data?.websiteSettings?.websites?.[0];

      // If onboarding has been started before
      if (savedSite) {
        setSite(savedSite);
        void preloadOnboarding(savedSite);
      } else {
        try {
          if (user?.email && window.tolt?.signup) {
            window.tolt.signup(user.email);
          }
        } catch {
          // Do nothing
        }

        if (userDomain && !isFreeDomain) {
          void preloadOnboarding(`https://${userDomain}`);
        } else {
          setReady(true);
        }
      }
    }
  }, [
    isFreeDomain,
    isMobile,
    isWebsiteReachable,
    onboardingWebsiteSettingsResponse.data?.websiteSettings?.websites,
    onboardingWebsiteSettingsResponse.fetching,
    site,
    startOnboarding,
    user,
    userDomain,
  ]);

  if (isMobile) {
    return (
      <div>
        <img src={background} alt="" className={styles.background} />
        <DeviceNotSupported
          loading={onboardingWebsiteSettingsResponse.fetching}
          isFirstTime={isFirstTime}
        />
      </div>
    );
  }

  return (
    <div>
      <img src={background} alt="" className={styles.background} />
      <InnerOnboarding
        ready={ready && timerComplete}
        onSubmit={onSubmit}
        onRetry={onRetry}
        step={step}
        setStep={setStep}
        site={site}
        imageLoaded={imageLoaded}
        setImageLoaded={setImageLoaded}
        imageLoadedError={imageLoadedError}
        setImageLoadedError={setImageLoadedError}
        hasFreeCredits={hasFreeCredits}
        isFreeDomain={isFreeDomain}
        isFirstTeam={userOrganizations?.length === 1}
        isHighValueCountry={isHighValueCountry}
      />
    </div>
  );
}
